import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Download } from 'lucide-react';
import { isUpdateRequired } from '@h2h/shared';
import { useAppConfig } from '../hooks/useQueries';
import { getAppVersion, type AppVersionInfo } from '../lib/appVersion';
import { APP_STORE_URL, openExternal } from '../lib/appStore';
import { lockAppChrome } from '../lib/overlayRoot';
import { EASE_OUT_IOS, SPRING_POP, prefersReducedMotion } from '../lib/motion';

/**
 * The force-update gate. When the remotely-controlled `config/app` doc says this
 * install is below the minimum allowed version/build, it drops a full-screen,
 * NON-dismissible cover over the entire app — no backdrop tap, no Escape, no
 * close button — with a single "Update" action that opens the App Store. The
 * only way past it is to actually update.
 *
 * Native-only by design: on the web a refresh already ships the latest deploy,
 * so {@link getAppVersion} reports `native: false` and this renders nothing
 * (which also means the web admin dashboard can never lock itself out).
 *
 * It fails OPEN — an absent/disabled config, a not-yet-loaded config, or an
 * unreadable native version never blocks (see {@link isUpdateRequired}) — so it
 * can't keep anyone out of the app by accident.
 */
export function ForceUpdateGate() {
  const { data: appConfig } = useAppConfig();
  const [info, setInfo] = useState<AppVersionInfo | null>(null);

  useEffect(() => {
    let alive = true;
    void getAppVersion().then((v) => {
      if (alive) setInfo(v);
    });
    return () => {
      alive = false;
    };
  }, []);

  const cfg = appConfig?.forceUpdate;
  const required =
    !!info?.native && isUpdateRequired(cfg, { version: info.version, build: info.build });

  // Hide the persistent app chrome (tab bar) while the gate is up.
  useEffect(() => {
    if (!required) return;
    return lockAppChrome();
  }, [required]);

  if (typeof document === 'undefined' || !required) return null;

  const reduce = prefersReducedMotion();
  const storeUrl = cfg?.iosUrl?.trim() || APP_STORE_URL;
  const title = cfg?.title?.trim() || 'Update required';
  const message =
    cfg?.message?.trim() ||
    'You’re on an older version of HeadToHead. Update to the latest version to keep playing.';
  const latest = cfg?.latestVersion?.trim();

  return createPortal(
    <motion.div
      // Above everything (chrome 50 · overlay 100 · toast 120) and rooted on the
      // body so no ancestor stacking context can let other UI peek through.
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-night/85 p-6 backdrop-blur-md"
      role="alertdialog"
      aria-modal="true"
      aria-label={title}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.22, ease: EASE_OUT_IOS }}
    >
      <motion.div
        className="relative w-full max-w-[340px] rounded-[24px] bg-card p-6 text-center shadow-[0_30px_60px_-20px_rgba(0,0,0,0.5)]"
        initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.94, y: 8 }}
        animate={reduce ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
        transition={reduce ? { duration: 0.16, ease: EASE_OUT_IOS } : SPRING_POP}
      >
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-accent-wash text-accent-ink">
          <Download size={26} />
        </div>

        <h2 className="mt-4 text-[19px] font-extrabold tracking-[-0.02em] text-ink">{title}</h2>
        <p className="mt-2 text-[14px] font-medium leading-[1.45] text-ink2">{message}</p>
        {latest && (
          <p className="mt-1 text-[12px] font-semibold text-ink3">Version {latest} is available</p>
        )}

        <button
          type="button"
          onClick={() => openExternal(storeUrl)}
          className="tap mt-5 w-full rounded-2xl bg-night py-3.5 text-[15px] font-bold text-white"
        >
          Update now
        </button>
      </motion.div>
    </motion.div>,
    document.body
  );
}
